import React, { useCallback } from 'react';

import { getDecimalsPlacesLength } from '../../utils';
import { TextField } from '../TextField';
import type { TextFieldProps } from '../TextField/TextField.types';

// ─────────────────────────────────────────────────────────────────────────────

export const NumberField = (props: NumberFieldProps) => {
  const {
    keyboardType = 'numeric',
    value,
    defaultValue,
    min = Number.NEGATIVE_INFINITY,
    max = Number.POSITIVE_INFINITY,
    maxDecimals = Number.POSITIVE_INFINITY,
    ...restProps
  } = props;

  // ────────────────────────────────────────────────────────────────────────────────

  const validateTextAsNumber = useCallback(
    (numericValue: string) => {
      const textAsNumber = Number(numericValue);
      const containsOnlyNumericChars = Number.isFinite(textAsNumber);
      const hasWhitespace = /\s/g.test(numericValue);
      const startsWithNegate =
        numericValue.length === 1 && numericValue.startsWith('-');
      const decimalsNumber = getDecimalsPlacesLength(numericValue);
      const hasAcceptableDecimalsNumber = decimalsNumber <= maxDecimals;
      const isLargerOrEqualToMin = textAsNumber >= min;
      const isSmallerOrEqualToMax = textAsNumber <= max;

      return (
        !hasWhitespace &&
        (startsWithNegate || containsOnlyNumericChars) &&
        hasAcceptableDecimalsNumber &&
        isLargerOrEqualToMin &&
        isSmallerOrEqualToMax
      );
    },
    [max, maxDecimals, min],
  );

  // ────────────────────────────────────────────────────────────────────────────────

  const convertedValue =
    typeof value !== 'undefined' ? String(value) : undefined;
  const convertedDefaultValue =
    typeof defaultValue !== 'undefined' ? String(defaultValue) : undefined;

  return (
    <TextField
      keyboardType={keyboardType}
      value={convertedValue}
      defaultValue={convertedDefaultValue}
      onChangeTextGuard={validateTextAsNumber}
      {...restProps}
    />
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type NumberFieldProps = Omit<
  TextFieldProps,
  'keyboardType' | 'value' | 'defaultValue' | 'onChangeTextGuard'
> & {
  readonly keyboardType?: 'number-pad' | 'decimal-pad' | 'numeric';
  readonly value?: number;
  readonly defaultValue?: number;
  readonly min?: number;
  readonly max?: number;
  readonly maxDecimals?: number;
};
